import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LightboxService, StorageService, StrapiService, ToastService } from '@core/services';
import { AuthService } from '@customer/services';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'oneok/environments/environment';

@Component({
  selector: 'oke-login',
  imports: [AsyncPipe, RouterLink],
  templateUrl: './login.html',
  styleUrl: './login.scss'
})
export class Login implements OnInit, AfterViewInit, OnDestroy {
  private readonly _authSvc = inject(AuthService);
  private readonly _storageSvc = inject(StorageService);
  private readonly _toastSvc = inject(ToastService);
  private readonly _strapiSvc = inject(StrapiService);
  private readonly _route = inject(ActivatedRoute);
  bodyElem = document.body;
  strapiContentSource = new BehaviorSubject<string | null>(null);
  strapiContent$ = this.strapiContentSource.asObservable();
  readonly $externalLoginSvgUrl: WritableSignal<string> = signal('/assets/svg/right-to-bracket.svg');
  readonly $internalLoginSvgUrl: WritableSignal<string> = signal('/assets/svg/key.svg');
  forgotPasswordUrl: string;
  private readonly _$isPending: WritableSignal<boolean> = signal(false);
  readonly $isPending: Signal<boolean> = this._$isPending.asReadonly();
  private readonly _$isSecureAuthError: WritableSignal<boolean> = signal(false);
  readonly $isSecureAuthError: Signal<boolean> = this._$isSecureAuthError.asReadonly();

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this._storageSvc.setCurrentPage();
    });
  }

  ngOnDestroy(): void {
    const recaptchaEl = document.getElementsByClassName('grecaptcha-badge')[0] as HTMLElement;
    if (recaptchaEl) {
      recaptchaEl.style.visibility = 'hidden';
    }
  }

  getPageContent(): void {
    const strapiPageLogin = JSON.parse(sessionStorage.getItem('strapiPageLogin') as string);
    if (!strapiPageLogin) {
      this._strapiSvc.getLoginContent().subscribe(content => {
        sessionStorage.setItem('strapiPageLogin', JSON.stringify(content));
        this.strapiContentSource.next(content);
      });
    } else {
      this.strapiContentSource.next(strapiPageLogin);
    }
  }

  initializeComponent(): void {
    this.forgotPasswordUrl = this._authSvc.dTest(environment.forgotPasswordUrl);
    this._toastSvc.dismissAll();
    const recaptchaEl = document.getElementsByClassName('grecaptcha-badge')[0] as HTMLElement;
    if (recaptchaEl) {
      recaptchaEl.style.visibility = 'hidden';
    }
    this.initializeExternalView();
    this._authSvc.logout();
    if (this._route.snapshot.queryParamMap.get('autoLogin') === 'true') {
      this._$isPending.set(true);
      this.logOnSecure();
    }
  }

  // Remove internal styling from body of app (Note: Should be modified to using NG Class for conditional styling)
  initializeExternalView(): void {
    this.bodyElem.className = 'is-unauthenticated';
  }

  /**
   * @description Initiates OIDC secure authentication
   * @param clickEvt Click event from button
   * @param mode Authentication mode (external/internal)
   */
  async logOnSecure(): Promise<void> {
    // Prevent double-clicks
    if (this.$isPending?.() ?? false) {
      return;
    }
    this._$isSecureAuthError.set(false);
    this._$isPending.set(true);
    try {
      await this._authSvc.loginUsers();
    } catch (e) {
      console.error(e);
      this._$isSecureAuthError.set(true);
      this._$isPending.set(false);
    } finally {
      this._$isPending.set(false);
    }
  }
}
